import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@core/ngrx-legacy';
import {
  AddEmployeeForPreonboardingPayload,
  BulkUpdateMembersPayload,
  InviteMemberPayload,
  ManageSeatsResponse,
  MemberDataUpdate,
  SeatKind,
  TeamMembersParams,
  TellentMember,
  TellentMemberResponse,
  TellentMemberRow,
  TellentOrgAbilities,
  TellentOrgAbilitiesExtended,
} from '@core/tellent-types';

import { resolveMemberSeatCellText, resolveMemberStatus } from '../helpers';
import type { ManageSeatsPayload } from '../types';
import { TellentMembersStoreKey } from '../types';
import { TellentAppsSelectors } from './tellent-apps.store';

const idSelector = ({ guid }: { guid: string }) => guid;

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<TellentMember>()(
  TellentMembersStoreKey.TellentMembers,
  {
    idSelector,
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      return {
        inviteTellentMember: (
          state,
          payload: InviteMemberPayload | AddEmployeeForPreonboardingPayload,
        ) => adapter.markAsPending(state),
        inviteTellentMemberSuccess: (state, payload: TellentMember[]) =>
          adapter.markAsLoaded(state),
        inviteTellentMemberFail: ngrxEvent<HttpErrorResponse>(),

        inviteExistingMember: (state, payload: BulkUpdateMembersPayload) =>
          adapter.markAsPending(state),
        inviteExistingMemberSuccess: (state, payload: TellentMember[]) =>
          adapter.markAsLoaded(state),
        inviteExistingMemberFail: ngrxEvent<HttpErrorResponse>(),

        inviteAppMember: (
          state,
          payload: InviteMemberPayload | AddEmployeeForPreonboardingPayload,
        ) => adapter.markAsPending(state),
        inviteAppMemberSuccess: (state, payload: TellentMember[]) => adapter.markAsLoaded(state),
        inviteAppMemberFail: ngrxEvent<HttpErrorResponse>(),

        fetchPaginatedCollection: (state, payload: { params: TeamMembersParams; add?: boolean }) =>
          adapter.markAsPending(state, { scope: payload.params.scope, entities: [] }),

        deactivateMember: (state, payload: { guid: string }) => adapter.markAsPending(state),
        deactivateMemberSuccess: (state, payload: TellentMemberResponse) =>
          pipeState(
            state,
            next => adapter.updateOne(next, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        deactivateMemberFailed: ngrxEvent<HttpErrorResponse>(),

        bulkDeactivate: (state, payload: { guids: string[] }) => adapter.markAsPending(state),
        bulkDeactivateSuccess: (state, payload: { data: TellentMember[] }) =>
          pipeState(
            state,
            next => adapter.updateMany(next, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        bulkDeactivateFailed: ngrxEvent<HttpErrorResponse>(),

        bulkReactivate: (state, payload: { guids: string[] }) => adapter.markAsPending(state),
        bulkReactivateSuccess: (state, payload: { data: TellentMember[] }) =>
          pipeState(
            state,
            next => adapter.updateMany(next, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        bulkReactivateFailed: ngrxEvent<HttpErrorResponse>(),

        bulkResetPassword: (state, payload: { guids: string[] }) => adapter.markAsPending(state),
        bulkResetPasswordSuccess: state => adapter.markAsLoaded(state),
        bulkResetPasswordFailed: ngrxEvent<HttpErrorResponse>(),

        bulkResendInvitations: (state, payload: { guids: string[] }) =>
          adapter.markAsPending(state),
        bulkResendInvitationsSuccess: state => adapter.markAsLoaded(state),
        bulkResendInvitationsFailed: ngrxEvent<HttpErrorResponse>(),

        bulkInviteViaEmail: (state, payload: { payloads: BulkUpdateMembersPayload[] }) =>
          adapter.markAsPending(state),
        bulkInviteViaEmailSuccess: state => adapter.markAsLoaded(state),
        bulkInviteViaEmailFail: ngrxEvent<HttpErrorResponse>(),

        bulkGenerateLogins: (state, payload: { payloads: BulkUpdateMembersPayload[] }) =>
          adapter.markAsPending(state),
        bulkGenerateLoginsSuccess: state => adapter.markAsLoaded(state),
        bulkGenerateLoginsFail: ngrxEvent<HttpErrorResponse>(),

        deleteMember: (state, payload: { guid: string }) => adapter.markAsPending(state),
        deleteMemberSuccess: (state, payload: { success: true }) =>
          pipeState(state, next => adapter.markAsLoaded(next)),
        deleteMemberFailed: ngrxEvent<HttpErrorResponse>(),

        reactivateMember: (state, payload: { guid: string }) => adapter.markAsPending(state),
        reactivateMemberSuccess: (state, payload: TellentMemberResponse) =>
          adapter.markAsLoaded(state),
        reactivateMemberFailed: ngrxEvent<HttpErrorResponse>(),

        sendInvitation: (state, payload: { guid: string }) => adapter.markAsPending(state),
        sendInvitationSuccess: (state, payload: { guid: string }) => adapter.markAsLoaded(state),
        sendInvitationFailed: ngrxEvent<HttpErrorResponse>(),

        manageSeats: (state, payload: ManageSeatsPayload) => adapter.markAsPending(state),
        manageSeatsSuccess: (state, payload: ManageSeatsResponse) => adapter.markAsLoaded(state),
        manageSeatsFailed: ngrxEvent<HttpErrorResponse>(),

        updateMember: (state, payload: MemberDataUpdate) => adapter.markAsPending(state),
        updateMemberSuccess: (state, payload: TellentMemberResponse) =>
          pipeState(
            state,
            next => adapter.updateOne(next, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        updateMemberFail: ngrxEvent<HttpErrorResponse>(),

        sendPasswordReset: (state, payload: { guid: string }) => adapter.markAsPending(state),
        sendPasswordResetSuccess: state => adapter.markAsLoaded(state),
        sendPasswordResetFail: ngrxEvent<HttpErrorResponse>(),

        updateAbilities: (
          state,
          payload: {
            guid: string;
            abilities: Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>;
          },
        ) => adapter.markAsPending(state),
        updateAbilitiesSuccess: (state, payload: TellentMemberResponse) =>
          pipeState(
            state,
            next => adapter.updateOne(next, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        updateAbilitiesFail: ngrxEvent<HttpErrorResponse>(),

        addGroups: (state, payload: { memberGuid: string; groupsGuids: string[] }) =>
          adapter.markAsPending(state),
        addGroupsSuccess: (state, payload: TellentMemberResponse) =>
          pipeState(
            state,
            next => adapter.updateOne(next, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        addGroupsFailed: (state, payload: { error: string }) => adapter.markAsLoaded(state),

        removeGroups: (state, payload: { memberGuid: string; groupsGuids: string[] }) =>
          adapter.markAsPending(state),
        removeGroupsSuccess: (state, payload: TellentMemberResponse) =>
          pipeState(
            state,
            next => adapter.updateOne(next, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        removeGroupsFailed: (state, payload: { error: string }) => adapter.markAsLoaded(state),

        fetchAppMembersPaginated: (state, payload: { params: TeamMembersParams; add?: boolean }) =>
          adapter.markAsPending(state, { scope: payload.params.scope, entities: [] }),
        fetchAppMembersPaginatedSuccess: defaultActions.fetchPaginatedCollectionSuccess,
        fetchAppMembersPaginatedFailed: defaultActions.fetchPaginatedCollectionFailed,

        generateTellentPasswords: (state, payload: { membersGuids: string[] }) =>
          adapter.markAsPending(state),
        generateTellentPasswordsSuccess: (state, payload: Blob) => adapter.markAsLoaded(state),
        generateTellentPasswordsFail: ngrxEvent<HttpErrorResponse>(),

        generateAppPasswords: (state, payload: { membersGuids: string[] }) =>
          adapter.markAsPending(state),
        generateAppPasswordsSuccess: (state, payload: Blob) => adapter.markAsLoaded(state),
        generateAppPasswordsFail: ngrxEvent<HttpErrorResponse>(),

        changeLoginMethod: (state, payload: { email: string; guid: string }) =>
          adapter.markAsPending(state),
        changeLoginMethodSuccess: (state, payload: TellentMemberResponse) =>
          pipeState(
            state,
            next => adapter.updateOne(next, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        changeLoginMethodFailed: ngrxEvent<HttpErrorResponse>(),
      };
    },
  },
);

const teamMembersRowSelector = memoizeSelector(
  (props: import('@core/ngrx-legacy').MetaScopeOptions) =>
    createSelector(
      selectors.entitiesWithPagination({ scope: props.scope }),
      TellentAppsSelectors.entities(),
      ({ rows }, apps): TellentMemberRow[] =>
        rows.map(member => ({
          ...member,
          id: member.guid, // id is required for the data-table
          status: resolveMemberStatus(member),
          hasFullAccess: member.abilities.includes(TellentOrgAbilities.ManageTellent),
          recruitment: resolveMemberSeatCellText(member, apps, SeatKind.Recruitment),
          coreHr: resolveMemberSeatCellText(member, apps, SeatKind.CoreHR),
        })),
    ),
);

const teamMemberById = memoizeSelector((memberGuid: string) =>
  createSelector(selectors.byId(memberGuid), (member): TellentMember | null =>
    member
      ? {
          ...member!,
          status: resolveMemberStatus(member!),
          owner: !!member?.owner,
          hasFullAccess: member.abilities.includes(TellentOrgAbilities.ManageTellent),
        }
      : null,
  ),
);

const TellentMembersSelectors = {
  ...selectors,
  teamMembersRowSelector,
  teamMemberById,
};

export {
  actions as TellentMembersActions,
  TellentMembersSelectors,
  reducer as TellentMembersReducer,
  namespace as TellentMembersNamespace,
};
